import { Container, Graphics, Rectangle, Text } from "pixi.js"
import TextInput from "pixi-text-input"

import { BUTTON_SIZES } from "./ui_tokens"
import { TextRenderer } from "../text/text_renderer"
import { createButton, createIconButton } from "./button_factory"
import { UiButton } from "./ui_button"
import { MAX_CHAT_LENGTH } from "./chat_input_model"
import {
    CHAT_CORNER,
    INPUT_PANEL_ALPHA,
    PANEL_FILL,
    RIM_ACTIVE,
    SHADOW_ALPHA,
    SHADOW_COLOR,
    SHADOW_OFFSET,
} from "./chat_theme"

// The chat composer: the way to SAY something on a device with no keyboard.
//
// The inline field beside it (gui/typing.ts) is drawn in pixi and fed from document keydowns, which
// is everything a phone cannot give: there is no Enter to open it with, no Escape to leave it with,
// and a soft keyboard with autocorrect does not produce the keystrokes it reads. So touch gets this
// instead -- a real DOM <input> over the canvas, exactly as gui/name_prompt.ts and
// intro/login_field.ts do, which is the only thing that raises a phone's keyboard at all.
//
// It docks at the TOP of the screen, which is the one band both platforms leave alone:
//
//   - Android shrinks window.innerHeight when the keyboard opens, so index.ts's resize() relays the
//     whole canvas out into the strip above it. Whatever was at the bottom is squeezed; the top
//     survives.
//   - iOS does not resize anything -- the keyboard simply covers the bottom half of the page (more
//     than half, in landscape) -- and WebKit pans to reveal a focused field. A field already at the
//     top is never covered and never makes the page move.
//
// It is also the one band free of thumbs: the move and fire sticks own the bottom corners, with the
// readied-item row and the camp roster between them.
export const COMPOSER_PANEL_W = 560
export const COMPOSER_PANEL_H = 52
// The virtual screen everything is laid out in (index.ts VIRTUAL_W). Centred on it rather than on
// the HUD's own right edge, so the panel sits under the middle of the screen the player is holding.
const SCREEN_W = 800
export const COMPOSER_PANEL_X = (SCREEN_W - COMPOSER_PANEL_W) / 2
// Clear of the top-right chip row / control bar (y 6, ~20 tall), which stays reachable-looking
// behind the scrim rather than being covered by the panel.
export const COMPOSER_PANEL_Y = 34
export const COMPOSER_PANEL_BOTTOM = COMPOSER_PANEL_Y + COMPOSER_PANEL_H

const PAD = 11
const GAP = 8
const FIELD_H = BUTTON_SIZES.medium.height
const SEND_W = 74
const SEND_H = FIELD_H
const BUTTON_CORNER = 6
const BUTTON_IDLE_RIM = 0x6b4f38
const CLOSE_W = 28

const FIELD_W = COMPOSER_PANEL_W - PAD * 2 - SEND_W - CLOSE_W - GAP * 2
const FIELD_X = COMPOSER_PANEL_X + PAD
const FIELD_Y = COMPOSER_PANEL_Y + PAD
const SEND_X = FIELD_X + FIELD_W + GAP
const CLOSE_X = SEND_X + SEND_W + GAP

// How far the dim reaches. Generous on every side: the stage is 800 x ~462 and is stretched
// vertically (see the canvas-resolution notes in index.ts), and a scrim that stops short of an edge
// leaves a live strip of world the player can swing at while they are typing.
const SCRIM = new Rectangle(-60, -60, SCREEN_W + 120, 620)
const SCRIM_ALPHA = 0.28
// Dark glass, a shade off the input field's so the panel reads as sitting on the scrim.

export interface ChatComposerProps {
    // A line the player means to send. Never called with an empty string.
    onSend: (message: string) => void
    // The player backed out: the scrim, the ✕, or Escape on a keyboard that has one. The half
    // written line goes.
    onDismiss: () => void
}

export class ChatComposer extends Container {
    private onSend: (message: string) => void
    private onDismiss: () => void
    private scrim: Graphics
    private shadow: Graphics
    private panel: Graphics
    private input: any
    private sendButton: UiButton
    private closeButton: UiButton
    private opened: boolean = false

    constructor({ onSend, onDismiss }: ChatComposerProps) {
        super()
        this.onSend = onSend
        this.onDismiss = onDismiss

        // The scrim is the gate as well as the dim: every pointer event over it stops here, so a tap
        // meant for the panel never also reaches the joystick or an entity underneath. Deliberately
        // NOT routed through GuiManager.mouseOverControl -- that latch is a single global boolean,
        // and pixi never delivers the matching pointerout to a widget hidden under a resting
        // pointer, which is how the world's mouse gets stuck off for a whole room. The world action
        // is gated on isChatComposerVisible() instead.
        this.scrim = new Graphics()
        this.scrim.beginFill(0x05070a, SCRIM_ALPHA)
        this.scrim.drawRect(SCRIM.x, SCRIM.y, SCRIM.width, SCRIM.height)
        this.scrim.endFill()
        this.scrim.interactive = true
        this.scrim.hitArea = SCRIM
        this.scrim.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.onDismiss()
        })
        this.scrim.on("pointermove", (event: any) => event.stopPropagation())
        this.addChild(this.scrim)

        this.shadow = new Graphics()
        this.panel = new Graphics()
        this.addChild(this.shadow, this.panel)
        this.drawPanel()

        // A tap anywhere on the panel itself is swallowed without dismissing: the gaps between the
        // field and the buttons are not a "cancel" target.
        const panelGate = new Container()
        panelGate.interactive = true
        panelGate.hitArea = new Rectangle(
            COMPOSER_PANEL_X,
            COMPOSER_PANEL_Y,
            COMPOSER_PANEL_W,
            COMPOSER_PANEL_H,
        )
        panelGate.on("pointerdown", (event: any) => event.stopPropagation())
        panelGate.on("pointermove", (event: any) => event.stopPropagation())
        this.addChild(panelGate)

        this.input = this.makeField()
        this.addChild(this.input)

        // Activate on press: dismissing the soft keyboard can move the release target.
        this.sendButton = createButton({ label: "Send", theme: "ember", preset: "primary", width: SEND_W,
            height: SEND_H, size: "medium", bold: true, activation: "press", onClick: () => this.submit() })
        this.closeButton = createIconButton({ icon: "✕", theme: "ember", width: CLOSE_W, height: SEND_H,
            size: "medium", activation: "press", onClick: () => this.onDismiss() })
        this.sendButton.position.set(SEND_X, FIELD_Y)
        this.closeButton.position.set(CLOSE_X, FIELD_Y)
        this.addChild(this.sendButton, this.closeButton)

        this.visible = false
    }

    private drawPanel() {
        this.shadow.clear()
        this.shadow.beginFill(SHADOW_COLOR, SHADOW_ALPHA)
        this.shadow.drawRoundedRect(
            COMPOSER_PANEL_X + SHADOW_OFFSET,
            COMPOSER_PANEL_Y + SHADOW_OFFSET,
            COMPOSER_PANEL_W,
            COMPOSER_PANEL_H,
            CHAT_CORNER,
        )
        this.shadow.endFill()

        this.panel.clear()
        this.panel.lineStyle(1.5, RIM_ACTIVE, 0.9)
        this.panel.beginFill(PANEL_FILL, INPUT_PANEL_ALPHA)
        this.panel.drawRoundedRect(
            COMPOSER_PANEL_X,
            COMPOSER_PANEL_Y,
            COMPOSER_PANEL_W,
            COMPOSER_PANEL_H,
            CHAT_CORNER,
        )
        this.panel.endFill()
    }

    // The field itself. Same two departures from the library's defaults that name_prompt.ts and
    // login_field.ts document at length: the real <input> stays on screen (substituteText off) and
    // the box is ours.
    private makeField() {
        const input = new TextInput({
            input: {
                fontFamily: "Arial, sans-serif",
                // 16px is the floor Safari zooms a focused field up to. index.html's viewport meta
                // pins the scale so it would not zoom anyway, but a field that is already big
                // enough cannot be caught out by a page that loses that pin.
                fontSize: "16px",
                lineHeight: `${FIELD_H}px`,
                height: `${FIELD_H}px`,
                width: `${FIELD_W}px`,
                boxSizing: "border-box",
                padding: "0 10px",
                color: "#fff3e0",
                caretColor: "#f2a94e",
                multiline: false,
            },
            box: (w: number, h: number, state: string) => {
                const g = new Graphics()
                g.lineStyle(1.5, state === "FOCUSED" ? RIM_ACTIVE : BUTTON_IDLE_RIM, state === "FOCUSED" ? 1 : 0.7)
                g.beginFill(0x120e0b, 0.95)
                g.drawRoundedRect(0, 0, w, h, BUTTON_CORNER)
                g.endFill()
                return g
            },
        })

        // The library builds its pixi Text stand-in before we can opt out, and _destroySurrogate()
        // leaves _surrogate_mask behind -- an opaque black Graphics that would paint a slab over the
        // field. (Same pair of fixes as login_field.ts and name_prompt.ts.)
        input.substituteText = false
        const orphanedMask: Graphics | undefined = input._surrogate_mask
        if (orphanedMask) {
            input.removeChild(orphanedMask)
            orphanedMask.destroy()
            input._surrogate_mask = null
        }

        input.x = FIELD_X
        input.y = FIELD_Y
        // The server drops anything longer, and the inline field caps the same line at the same
        // length, so a phone is not the one place a message can be typed and then truncated.
        input.maxLength = MAX_CHAT_LENGTH
        input.placeholder = "Say something…"

        const dom: HTMLInputElement = input._dom_input
        // ::placeholder and ::selection colours for it live in index.html.
        dom.className = "samsara-input"
        dom.setAttribute("aria-label", "Chat message")
        // Chat is prose, so unlike a name field this one keeps the phone's help: sentence case and
        // autocorrect on.
        dom.setAttribute("autocapitalize", "sentences")
        dom.setAttribute("autocomplete", "off")
        // The one key a phone keyboard shows for "done". Without this it offers a newline the field
        // cannot hold, on a line nothing would send.
        dom.setAttribute("enterkeyhint", "send")

        input.on("keyup", (keycode: number) => {
            if (keycode === 13) {
                this.submit()
            }
            if (keycode === 27) {
                this.onDismiss()
            }
        })

        return input
    }

    private submit() {
        const value = (this.input.text || "").trim()
        if (!value) {
            // An empty line is not a message. Treated as backing out, which is what a player who
            // taps Send on an empty field means.
            this.onDismiss()
            return
        }
        this.onSend(value)
    }

    /**
     * Show or hide the DOM <input> itself.
     *
     * pixi-text-input syncs the real <input>'s css display from inside render(), i.e. only while it
     * is being drawn -- so the frame this container's `visible` goes false is the library's last
     * chance to hide the field. Miss it and the half-typed line stays painted over the game for
     * good. Showing is handed back to the library (it places the node and reveals it in the same
     * pass; a node it has not placed yet still sits at the page's top-left), which only needs a
     * reason to re-sync -- our hide was invisible to it. Cf. login_field.ts, which carries the same
     * pair.
     */
    private setDomVisible(visible: boolean) {
        if (visible) {
            this.input._previous.world_visible = false
            return
        }
        this.input.blur()
        this.input._setDOMInputVisible(false)
    }

    public isOpen(): boolean {
        return this.opened
    }

    public text(): string {
        return (this.input.text || "").trim()
    }

    public open() {
        this.opened = true
        this.sendButton.resetInteraction()
        this.closeButton.resetInteraction()
        // Every open starts on a blank line: a draft left over from a conversation two camps ago is
        // not something a player wants to find in the field.
        this.input.text = ""
        this.visible = true
        this.setDomVisible(true)
        // Deferred a tick on purpose: a focus() issued while the browser is still dispatching the
        // pointerdown that opened this is handed straight back to the canvas, which on a phone means
        // the keyboard never comes up at all. (The trap login_field.ts hit.)
        setTimeout(() => {
            if (this.opened) {
                this.input.focus()
            }
        }, 0)
    }

    public close() {
        this.opened = false
        this.input.text = ""
        // Before `visible` goes false, for the reason setDomVisible explains.
        this.setDomVisible(false)
        this.visible = false
    }

    /**
     * Detach the DOM node. pixi-text-input appends it to document.body on 'added' and removes it on
     * 'removed', so it outlives the canvas unless the field leaves the stage.
     */
    public dispose() {
        const dom: HTMLInputElement = this.input._dom_input
        try {
            this.input.parent?.removeChild(this.input)
        } catch (e) {
            /* ignore -- the node is detached below either way */
        }
        if (dom && dom.parentNode) {
            dom.parentNode.removeChild(dom)
        }
        this.input._dom_added = false
    }
}
